<script lang="ts">
  import { t } from '$core/i18n';
  import {
    computeStreak,
    buildSnapshot,
    lastDaysActivity,
    startOfWeekKey,
    type DayActivity
  } from '$features/goals';
  import {
    getOrCreateStudyGoal,
    updateStudyGoal
  } from '$db/local/repositories';
  import { dayKey } from '$features/forecast';
  import type { MacroSession, StudyGoal } from '$domain/types';
  import { Button } from '$ui/primitives/Button';
  import { Input } from '$ui/primitives/Input';
  import { ProgressBar } from '$ui/primitives/ProgressBar';

  let sessions = $state<MacroSession[]>([]);
  let goal = $state<StudyGoal | null>(null);
  let dailyDraft = $state(60);
  let weeklyDraft = $state(300);
  let activeDaysDraft = $state<number[]>([1, 2, 3, 4, 5]);
  let saving = $state(false);
  let justSaved = $state(false);

  const today = dayKey(new Date());

  let streak = $derived(
    sessions.length > 0 || goal !== null
      ? computeStreak(sessions, today)
      : { current: 0, longest: 0, todayHasActivity: false, yesterdayHasActivity: false, inGrace: false }
  );

  let snapshot = $derived(
    buildSnapshot(
      sessions,
      today,
      goal?.dailyMinutes ?? 0,
      goal?.weeklyMinutes ?? 0
    )
  );

  let recentDays: DayActivity[] = $derived(
    lastDaysActivity(sessions, today, 14, goal?.activeDays ?? [])
  );

  async function refresh() {
    const { listMacroSessionsPage } = await import('$db/local/repositories');
    const page = await listMacroSessionsPage({ limit: 10000 });
    sessions = page.sessions;
    goal = await getOrCreateStudyGoal();
    dailyDraft = goal.dailyMinutes;
    weeklyDraft = goal.weeklyMinutes;
    activeDaysDraft = [...goal.activeDays];
  }

  $effect(() => {
    void refresh();
  });

  async function save() {
    saving = true;
    justSaved = false;
    try {
      goal = await updateStudyGoal({
        dailyMinutes: dailyDraft,
        weeklyMinutes: weeklyDraft,
        activeDays: activeDaysDraft
      });
      justSaved = true;
      setTimeout(() => (justSaved = false), 1500);
    } finally {
      saving = false;
    }
  }

  function toggleActiveDay(dow: number) {
    if (activeDaysDraft.includes(dow)) {
      activeDaysDraft = activeDaysDraft.filter((d) => d !== dow);
    } else {
      activeDaysDraft = [...activeDaysDraft, dow].sort((a, b) => a - b);
    }
  }

  function weekdayShort(dow: number): string {
    const keys = [
      'goals.weekdays.sun',
      'goals.weekdays.mon',
      'goals.weekdays.tue',
      'goals.weekdays.wed',
      'goals.weekdays.thu',
      'goals.weekdays.fri',
      'goals.weekdays.sat'
    ] as const;
    return t(keys[dow]!);
  }

  function fmtDateCell(day: DayActivity): { d: string; m: string } {
    const [y, mo, da] = day.date.split('-').map(Number);
    const dt = new Date(y!, (mo ?? 1) - 1, da ?? 1);
    const d = String(dt.getDate()).padStart(2, '0');
    const m = dt
      .toLocaleDateString(undefined, { month: 'short' })
      .replace('.', '')
      .slice(0, 3);
    return { d, m };
  }

  let weekStart = $derived(startOfWeekKey(today));
</script>

<section class="view">
  <header class="view-head">
    <h1 class="view-title">{t('goals.title')}</h1>
  </header>

  <section class="streak-section">
    <div class="streak-main">
      <span class="streak-number">{streak.current}</span>
      <span class="streak-label">
        {t('goals.streakDays', { n: streak.current })}
      </span>
    </div>
    <div class="streak-meta">
      {#if streak.inGrace}
        <span class="grace">{t('goals.inGrace')}</span>
      {:else if streak.current === 0}
        <span class="muted">{t('goals.streakBroken')}</span>
      {/if}
      <span class="muted">
        {t('goals.longest')}: {streak.longest}
      </span>
    </div>
  </section>

  <section class="progress-section">
    <div class="progress-row">
      <div class="progress-label">
        <span>{t('goals.todayProgress')}</span>
        <span class="muted">
          {t('goals.minutesOf', {
            done: snapshot.todayMinutes,
            target: snapshot.dailyTarget
          })}
        </span>
      </div>
      <ProgressBar value={snapshot.dailyProgress * 100} label={t('goals.todayProgress')} />
    </div>
    <div class="progress-row">
      <div class="progress-label">
        <span>{t('goals.weekProgress')}</span>
        <span class="muted">
          {t('goals.minutesOf', {
            done: snapshot.weekMinutes,
            target: snapshot.weeklyTarget
          })}
        </span>
      </div>
      <ProgressBar
        value={snapshot.weeklyProgress * 100}
        variant="success"
        label={t('goals.weekProgress')}
      />
    </div>
    <p class="hint">
      {t('goals.weekProgress')} · {weekStart}
    </p>
  </section>

  <section class="form-section">
    <div class="row-2">
      <Input
        type="number"
        min={0}
        max={1440}
        label={t('goals.dailyTarget')}
        bind:value={dailyDraft}
      />
      <Input
        type="number"
        min={0}
        max={10080}
        label={t('goals.weeklyTarget')}
        bind:value={weeklyDraft}
      />
    </div>

    <div class="field-block">
      <span class="field-label">{t('goals.activeDays')}</span>
      <div class="days-chips">
        {#each [1, 2, 3, 4, 5, 6, 0] as dow (dow)}
          <Button
            variant={activeDaysDraft.includes(dow) ? 'filled' : 'outline'}
            size="sm"
            onclick={() => toggleActiveDay(dow)}
            ariaLabel={weekdayShort(dow)}
          >
            {weekdayShort(dow)}
          </Button>
        {/each}
      </div>
    </div>

    <div class="form-actions">
      <Button variant="filled" loading={saving} disabled={saving} onclick={() => void save()}>
        {saving ? t('goals.saving') : t('goals.save')}
      </Button>
      {#if justSaved}
        <span class="saved">{t('goals.saved')}</span>
      {/if}
    </div>
  </section>

  <section class="days-section">
    <h2 class="section-title">{t('goals.last14Days')}</h2>
    {#if sessions.length === 0}
      <p class="hint">{t('goals.noSessions')}</p>
    {:else}
      <div class="days-grid">
        {#each recentDays as day (day.date)}
          {@const fmt = fmtDateCell(day)}
          <div
            class="day-cell"
            data-has-activity={day.minutes > 0 || undefined}
            data-active-day={day.isActiveDay || undefined}
            title={`${day.date} · ${day.minutes} min`}
          >
            <span class="day-d">{fmt.d}</span>
            <span class="day-m">{fmt.m}</span>
            {#if day.minutes > 0}
              <span class="day-min">{day.minutes}</span>
            {/if}
          </div>
        {/each}
      </div>
    {/if}
  </section>
</section>

<style>
  .view {
    display: flex;
    flex-direction: column;
    gap: var(--space-6);
    max-width: 720px;
  }

  .view-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
  }

  .view-title {
    margin: 0;
    font-size: var(--text-h1);
    font-weight: var(--text-h1-weight);
    letter-spacing: var(--text-h1-track);
    line-height: var(--text-h1-lh);
    color: var(--color-text-primary);
    text-wrap: balance;
  }

  .section-title {
    margin: 0;
    font-size: var(--text-h2);
    font-weight: var(--text-h2-weight);
    letter-spacing: var(--text-h2-track);
    line-height: var(--text-h2-lh);
    color: var(--color-text-primary);
    text-wrap: balance;
  }

  .streak-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-5);
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-left: 4px solid var(--color-accent);
    border-radius: var(--radius-lg);
  }

  .streak-main {
    display: flex;
    align-items: baseline;
    gap: var(--space-3);
  }

  .streak-number {
    font-family: var(--font-mono);
    font-size: 3rem;
    font-weight: var(--weight-bold);
    color: var(--color-accent);
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }

  .streak-label {
    font-size: var(--text-body);
    color: var(--color-text-secondary);
  }

  .streak-meta {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: var(--space-1);
  }

  .grace {
    font-size: var(--text-caption);
    color: var(--color-warning);
    font-weight: var(--weight-medium);
  }

  .muted {
    font-size: var(--text-caption);
    color: var(--color-text-muted);
  }

  .progress-section {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding: var(--space-5);
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-lg);
  }

  .progress-row {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .progress-label {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: var(--text-body);
    color: var(--color-text-primary);
  }

  .hint {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--color-text-muted);
  }

  .form-section {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding: var(--space-5);
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-lg);
  }

  .row-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-3);
  }

  @media (max-width: 640px) {
    .row-2 {
      grid-template-columns: 1fr;
    }
  }

  .field-block {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .field-label {
    font-size: var(--text-label);
    font-weight: var(--text-label-weight);
    letter-spacing: var(--text-label-track);
    text-transform: uppercase;
    color: var(--color-text-secondary);
  }

  .days-chips {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }

  .form-actions {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .saved {
    font-size: var(--text-sm);
    color: var(--color-success);
    font-weight: var(--weight-medium);
  }

  .days-section {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .days-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: var(--space-2);
  }

  .day-cell {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: var(--space-2) 0;
    min-height: 52px;
    font-family: inherit;
    color: var(--color-text-muted);
    background: var(--color-surface-sunken);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-sm);
  }

  .day-cell[data-active-day] {
    border-style: dashed;
    border-color: var(--color-border-strong);
  }

  .day-cell[data-has-activity] {
    color: var(--color-accent-fg);
    background: var(--color-accent);
    border-color: var(--color-accent);
    border-style: solid;
  }

  .day-d {
    font-size: var(--text-sm);
    font-weight: var(--weight-semibold);
    font-variant-numeric: tabular-nums;
    line-height: 1;
  }

  .day-m {
    font-size: 10px;
    text-transform: uppercase;
    opacity: 0.75;
    line-height: 1;
  }

  .day-min {
    position: absolute;
    bottom: 2px;
    right: 3px;
    font-size: 9px;
    font-family: var(--font-mono);
    font-weight: var(--weight-bold);
    opacity: 0.9;
  }
</style>